import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang } from '../LanguageContext';
import { t } from '../i18n';
import FicheClient from './FicheClient';

const COULEUR = '#1C2833';
const ROUGE = '#C0392B';

const TYPES_CLIENT = ['GROSSISTE', 'DEMI GROS', 'DETAILLANT', 'DECORATIVE', 'ENTREPRENEUR'];
const STATUTS = ['Prospect', 'Client actif', 'Nouveau client', 'Client inactif'];
const POTENTIELS = ['Fort', 'Moyen', 'Faible'];

export default function ListeClientsManager({ onClose }) {
  const { lang, isRTL } = useLang();
  const [clients, setClients] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ficheClient, setFicheClient] = useState(null);

  // Filtres
  const [recherche, setRecherche] = useState('');
  const [filtreWilaya, setFiltreWilaya] = useState('Toutes');
  const [filtreType, setFiltreType] = useState('Tous');
  const [filtrePotentiel, setFiltrePotentiel] = useState('Tous');
  const [filtreStatut, setFiltreStatut] = useState('Tous');
  const [filtreComm, setFiltreComm] = useState('Tous');
  const [triFiltreActif, setTriFiltreActif] = useState(false);

  // Modals
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientAModifier, setClientAModifier] = useState(null);
  const [clientASupprimer, setClientASupprimer] = useState(null);
  const [clientAAssigner, setClientAAssigner] = useState(null);
  const [clientACorreger, setClientACorreger] = useState(null);
  const [gpsCorrection, setGpsCorrection] = useState('');
  const [commCible, setCommCible] = useState('');
  const [saving, setSaving] = useState(false);

  const [nouveauClient, setNouveauClient] = useState({
    nom: '', telephone: '', adresse: '', commune: '',
    wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect',
    potentiel: 'Moyen', gpsText: ''
  });

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const cSnap = await getDocs(collection(db, 'clients'));
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const parseGps = (text) => {
    const parts = text.replace(/\s/g, '').split(',');
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]))
      return { lat: parseFloat(parts[0]), lng: parseFloat(parts[1]) };
    return null;
  };

  const getNomComm = (id) => {
    const c = commerciaux.find(x => x.id === id);
    return c ? `${c.prenom} ${c.nom}` : id;
  };

  // Wilayas dynamiques
  const wilayas = ['Toutes', ...new Set(clients.map(c => c.wilaya || c.commune || 'Constantine').filter(Boolean))];

  // Filtrage
  const clientsFiltres = clients.filter(c => {
    const matchRecherche = !recherche ||
      c.nom?.toLowerCase().includes(recherche.toLowerCase()) ||
      c.telephone?.includes(recherche) ||
      c.commune?.toLowerCase().includes(recherche.toLowerCase());
    const matchWilaya = filtreWilaya === 'Toutes' || c.wilaya === filtreWilaya || c.commune === filtreWilaya;
    const matchType = filtreType === 'Tous' || c.type === filtreType;
    const matchPotentiel = filtrePotentiel === 'Tous' || c.potentiel === filtrePotentiel;
    const matchStatut = filtreStatut === 'Tous' || c.statut === filtreStatut;
    const matchComm = filtreComm === 'Tous' ||
      (filtreComm === 'Non assigné' ? (!c.commercialIds || c.commercialIds.length === 0) : c.commercialIds?.includes(filtreComm));
    return matchRecherche && matchWilaya && matchType && matchPotentiel && matchStatut && matchComm;
  });

  const resetFiltres = () => {
    setRecherche('');
    setFiltreWilaya('Toutes');
    setFiltreType('Tous');
    setFiltrePotentiel('Tous');
    setFiltreStatut('Tous');
    setFiltreComm('Tous');
  };

  const nbFiltresActifs = [
    filtreWilaya !== 'Toutes',
    filtreType !== 'Tous',
    filtrePotentiel !== 'Tous',
    filtreStatut !== 'Tous',
    filtreComm !== 'Tous',
  ].filter(Boolean).length;

  // ACTIONS
  const ajouterClient = async () => {
    if (!nouveauClient.nom.trim()) return;
    setSaving(true);
    try {
      const gps = nouveauClient.gpsText ? parseGps(nouveauClient.gpsText) : null;
      const { gpsText, ...clientData } = nouveauClient;
      await addDoc(collection(db, 'clients'), {
        ...clientData, gps, commercialIds: [], createdAt: serverTimestamp()
      });
      setShowAddClient(false);
      setNouveauClient({ nom: '', telephone: '', adresse: '', commune: '', wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect', potentiel: 'Moyen', gpsText: '' });
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const modifierClient = async () => {
    if (!clientAModifier || !clientAModifier.nom?.trim()) return;
    setSaving(true);
    try {
      const { id, gpsText, ...data } = clientAModifier;
      if (gpsText) {
        const gps = parseGps(gpsText);
        if (gps) data.gps = gps;
      }
      await updateDoc(doc(db, 'clients', id), data);
      setClientAModifier(null);
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const supprimerClient = async () => {
    if (!clientASupprimer) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'clients', clientASupprimer.id));
      setClientASupprimer(null);
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const assignerCommercial = async () => {
    if (!commCible || !clientAAssigner) return;
    setSaving(true);
    try {
      const ids = clientAAssigner.commercialIds || [];
      if (!ids.includes(commCible)) {
        await updateDoc(doc(db, 'clients', clientAAssigner.id), { commercialIds: [...ids, commCible] });
      }
      setClientAAssigner(null);
      setCommCible('');
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const desassigner = async (clientId, commId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      const ids = (client.commercialIds || []).filter(x => x !== commId);
      await updateDoc(doc(db, 'clients', clientId), { commercialIds: ids });
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const enregistrerGps = async () => {
    if (!clientACorreger || !gpsCorrection.trim()) return;
    const gps = parseGps(gpsCorrection);
    if (!gps) { alert('Format invalide. Exemple : 36.3650, 6.6147'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clients', clientACorreger.id), { gps });
      setClientACorreger(null);
      setGpsCorrection('');
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  // Affichage fiche client
  if (ficheClient) return (
    <FicheClient
      client={ficheClient}
      user={{ uid: 'manager' }}
      isManager={true}
      onClose={() => { setFicheClient(null); charger(); }}
    />
  );

  const potentielColor = (p) => ({
    Fort: { bg: '#EAF3DE', color: '#3B6D11' },
    Faible: { bg: '#FCEBEB', color: '#A32D2D' },
    Moyen: { bg: '#FAEEDA', color: '#854F0B' },
  }[p] || { bg: '#FAEEDA', color: '#854F0B' });

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>📋 Liste Clients</div>
          <div style={styles.topbarSub}>{clientsFiltres.length} / {clients.length} clients</div>
        </div>
        <button style={styles.addBtn} onClick={() => setShowAddClient(true)}>+ Client</button>
      </div>

      {/* RECHERCHE */}
      <div style={styles.searchWrap}>
        <input
          style={styles.searchInput}
          placeholder="🔍 Rechercher par nom, téléphone, commune..."
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
        />
      </div>

      {/* BOUTON FILTRES */}
      <div style={styles.filtreToggleRow}>
        <button
          style={{ ...styles.filtreToggleBtn, background: triFiltreActif ? COULEUR : '#fff', color: triFiltreActif ? '#fff' : '#444' }}
          onClick={() => setTriFiltreActif(f => !f)}
        >
          ⚙️ Filtres {nbFiltresActifs > 0 ? `(${nbFiltresActifs})` : ''}
        </button>
        {nbFiltresActifs > 0 && (
          <button style={styles.resetBtn} onClick={resetFiltres}>✕ Réinitialiser</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>{clientsFiltres.length} résultat(s)</span>
      </div>

      {/* PANNEAU FILTRES */}
      {triFiltreActif && (
        <div style={styles.filtresPanel}>
          <div style={styles.filtreRow}>
            <label style={styles.filtreLabel}>Wilaya</label>
            <select style={styles.select} value={filtreWilaya} onChange={e => setFiltreWilaya(e.target.value)}>
              {wilayas.map(w => <option key={w}>{w}</option>)}
            </select>
          </div>
          <div style={styles.filtreRow}>
            <label style={styles.filtreLabel}>Type</label>
            <select style={styles.select} value={filtreType} onChange={e => setFiltreType(e.target.value)}>
              <option value="Tous">Tous</option>
              {TYPES_CLIENT.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div style={styles.filtreRow}>
            <label style={styles.filtreLabel}>Potentiel</label>
            <select style={styles.select} value={filtrePotentiel} onChange={e => setFiltrePotentiel(e.target.value)}>
              <option value="Tous">Tous</option>
              {POTENTIELS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div style={styles.filtreRow}>
            <label style={styles.filtreLabel}>Statut</label>
            <select style={styles.select} value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
              <option value="Tous">Tous</option>
              {STATUTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={styles.filtreRow}>
            <label style={styles.filtreLabel}>Commercial</label>
            <select style={styles.select} value={filtreComm} onChange={e => setFiltreComm(e.target.value)}>
              <option value="Tous">Tous</option>
              <option value="Non assigné">⚠️ Non assigné</option>
              {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* LISTE */}
      <div style={styles.listWrap}>
        {loading && <div style={styles.empty}>Chargement...</div>}

        {!loading && clientsFiltres.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 32 }}>👥</div>
            <div>Aucun client trouvé</div>
            {nbFiltresActifs > 0 && (
              <button style={styles.resetBtn2} onClick={resetFiltres}>Réinitialiser les filtres</button>
            )}
            <button style={styles.addClientEmptyBtn} onClick={() => setShowAddClient(true)}>+ Ajouter un client</button>
          </div>
        )}

        {clientsFiltres.map((c) => {
          const pc = potentielColor(c.potentiel);
          return (
            <div key={c.id} style={styles.clientCard}>
              {/* En-tête client */}
              <div style={styles.clientRow}>
                <div style={styles.clientAv}>{c.nom?.[0]?.toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={styles.clientNom}>{c.nom}</div>
                  <div style={styles.clientSub}>{c.type} · {c.commune || c.wilaya}</div>
                </div>
                <div style={{ ...styles.potentielBadge, background: pc.bg, color: pc.color }}>
                  {c.potentiel || 'Moyen'}
                </div>
              </div>

              {/* Statut + téléphone */}
              <div style={styles.clientMeta}>
                {c.statut && <span style={styles.statutBadge}>{c.statut}</span>}
                {c.telephone && <span style={styles.telText}>📞 {c.telephone}</span>}
              </div>

              {/* Commerciaux assignés */}
              {c.commercialIds?.length > 0 ? (
                <div style={styles.assignedWrap}>
                  {c.commercialIds.map(cid => (
                    <div key={cid} style={styles.assignedPill}>
                      <span style={{ fontSize: 11 }}>{getNomComm(cid)}</span>
                      <button style={styles.removeBtn} onClick={() => desassigner(c.id, cid)}>✕</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.nonAssigne}>⚠️ Non assigné</div>
              )}

              {/* Boutons actions */}
              <div style={styles.actionsRow}>
                <button style={styles.actionBtn} onClick={() => setFicheClient(c)}>👁 Voir</button>
                <button style={{ ...styles.actionBtn, color: '#185FA5' }} onClick={() => setClientAModifier({ ...c, gpsText: '' })}>✏️ Modifier</button>
                <button style={{ ...styles.actionBtn, color: '#3B6D11' }} onClick={() => { setClientAAssigner(c); setCommCible(''); }}>👤 Assigner</button>
                <button style={{ ...styles.actionBtn, color: ROUGE }} onClick={() => setClientASupprimer(c)}>🗑 Supprimer</button>
              </div>

              {/* GPS */}
              <div style={styles.gpsRow}>
                {c.gps ? (
                  <>
                    <span style={styles.gpsOk}>📍 GPS enregistré</span>
                    <button style={styles.gpsBtn} onClick={() => { setClientACorreger(c); setGpsCorrection(''); }}>Corriger</button>
                  </>
                ) : (
                  <button style={styles.gpsBtnWarning} onClick={() => { setClientACorreger(c); setGpsCorrection(''); }}>
                    ⚠️ Ajouter position GPS
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== MODAL NOUVEAU CLIENT ===== */}
      {showAddClient && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={styles.modalTitle}>➕ Nouveau client</div>
            {[
              { label: 'Nom *', key: 'nom', placeholder: 'Nom du client ou entreprise' },
              { label: 'Téléphone', key: 'telephone', placeholder: '0X XX XX XX XX' },
              { label: 'Commune', key: 'commune', placeholder: 'Ex: Constantine' },
              { label: 'Wilaya', key: 'wilaya', placeholder: 'Ex: Constantine' },
        
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={styles.formLabel}>{f.label}</div>
                <input style={styles.formInput} placeholder={f.placeholder}
                  value={nouveauClient[f.key]}
                  onChange={e => setNouveauClient(n => ({ ...n, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ marginBottom: 10 }}>
              <div style={styles.formLabel}>Type</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {TYPES_CLIENT.map(tp => (
                  <button key={tp} style={{ ...styles.chipBtn, ...(nouveauClient.type === tp ? styles.chipBtnActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, type: tp }))}>{tp}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.formLabel}>Statut</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUTS.map(s => (
                  <button key={s} style={{ ...styles.chipBtn, ...(nouveauClient.statut === s ? styles.chipBtnActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, statut: s }))}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.formLabel}>Potentiel</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {POTENTIELS.map(p => (
                  <button key={p} style={{ ...styles.chipBtn, ...(nouveauClient.potentiel === p ? styles.chipBtnActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, potentiel: p }))}>{p}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={styles.formLabel}>Position GPS (optionnel)</div>
              <input style={styles.formInput} placeholder="Ex: 36.3650, 6.6147"
                value={nouveauClient.gpsText}
                onChange={e => setNouveauClient(n => ({ ...n, gpsText: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                Clic droit sur Google Maps → Copier les coordonnées
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setShowAddClient(false)}>Annuler</button>
              <button style={{ ...styles.btnConfirm, opacity: nouveauClient.nom ? 1 : 0.4 }}
                onClick={ajouterClient} disabled={saving || !nouveauClient.nom}>
                {saving ? 'Enregistrement...' : '✅ Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL MODIFIER CLIENT ===== */}
      {clientAModifier && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={styles.modalTitle}>✏️ Modifier client</div>
            {[
              { label: 'Nom *', key: 'nom', placeholder: 'Nom du client' },
              { label: 'Téléphone', key: 'telephone', placeholder: '0X XX XX XX XX' },
              { label: 'Commune', key: 'commune', placeholder: 'Commune' },
              { label: 'Wilaya', key: 'wilaya', placeholder: 'Wilaya' },
        
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={styles.formLabel}>{f.label}</div>
                <input style={styles.formInput} placeholder={f.placeholder}
                  value={clientAModifier[f.key] || ''}
                  onChange={e => setClientAModifier(c => ({ ...c, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ marginBottom: 10 }}>
              <div style={styles.formLabel}>Type</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {TYPES_CLIENT.map(tp => (
                  <button key={tp} style={{ ...styles.chipBtn, ...(clientAModifier.type === tp ? styles.chipBtnActive : {}) }}
                    onClick={() => setClientAModifier(c => ({ ...c, type: tp }))}>{tp}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.formLabel}>Statut</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUTS.map(s => (
                  <button key={s} style={{ ...styles.chipBtn, ...(clientAModifier.statut === s ? styles.chipBtnActive : {}) }}
                    onClick={() => setClientAModifier(c => ({ ...c, statut: s }))}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={styles.formLabel}>Potentiel</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {POTENTIELS.map(p => (
                  <button key={p} style={{ ...styles.chipBtn, ...(clientAModifier.potentiel === p ? styles.chipBtnActive : {}) }}
                    onClick={() => setClientAModifier(c => ({ ...c, potentiel: p }))}>{p}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={styles.formLabel}>Nouvelle position GPS (laisser vide pour ne pas modifier)</div>
              <input style={styles.formInput} placeholder="Ex: 36.3650, 6.6147"
                value={clientAModifier.gpsText || ''}
                onChange={e => setClientAModifier(c => ({ ...c, gpsText: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setClientAModifier(null)}>Annuler</button>
              <button style={{ ...styles.btnConfirm, opacity: clientAModifier.nom ? 1 : 0.4 }}
                onClick={modifierClient} disabled={saving || !clientAModifier.nom}>
                {saving ? 'Enregistrement...' : '✅ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL SUPPRIMER ===== */}
      {clientASupprimer && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>🗑 Supprimer client</div>
            <div style={{ fontSize: 14, color: '#444', marginBottom: 8 }}>
              Confirmer la suppression de <strong>{clientASupprimer.nom}</strong> ?
            </div>
            <div style={{ fontSize: 12, color: '#A32D2D', marginBottom: 16, background: '#FCEBEB', padding: '8px 12px', borderRadius: 8 }}>
              ⚠️ Cette action est irréversible. Toutes les données de ce client seront perdues.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setClientASupprimer(null)}>Annuler</button>
              <button style={{ ...styles.btnConfirm, background: ROUGE }} onClick={supprimerClient} disabled={saving}>
                {saving ? 'Suppression...' : '🗑 Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL ASSIGNER ===== */}
      {clientAAssigner && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>👤 Assigner un commercial</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Client : <strong>{clientAAssigner.nom}</strong>
            </div>
            {commerciaux.map(c => (
              <button key={c.id}
                style={{ ...styles.commChoice, ...(commCible === c.id ? styles.commChoiceActive : {}) }}
                onClick={() => setCommCible(c.id)}>
                <div style={styles.commAv}>{c.prenom?.[0]}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{c.prenom} {c.nom}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{(c.wilayas || []).join(', ')}</div>
                </div>
                {clientAAssigner.commercialIds?.includes(c.id) && (
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#3B6D11' }}>✓ Déjà assigné</span>
                )}
              </button>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button style={styles.btnCancel} onClick={() => { setClientAAssigner(null); setCommCible(''); }}>Annuler</button>
              <button style={{ ...styles.btnConfirm, opacity: commCible ? 1 : 0.4 }}
                onClick={assignerCommercial} disabled={saving || !commCible}>
                {saving ? 'En cours...' : '✓ Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL GPS ===== */}
      {clientACorreger && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>📍 Position GPS</div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 14 }}>
              Client : <strong>{clientACorreger.nom}</strong>
            </div>
            <div style={styles.gpsInstructions}>
              <div>1. Ouvre <strong>Google Maps</strong></div>
              <div>2. Trouve l'adresse exacte du client</div>
              <div>3. Clic droit → <strong>"Copier les coordonnées"</strong></div>
              <div>4. Colle ici ↓</div>
            </div>
            <input style={{ ...styles.formInput, marginTop: 10, fontSize: 15 }}
              placeholder="Ex: 36.3650, 6.6147"
              value={gpsCorrection}
              onChange={e => setGpsCorrection(e.target.value)}
              autoFocus />
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 6, marginBottom: 16 }}>
              Format : latitude, longitude
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => { setClientACorreger(null); setGpsCorrection(''); }}>Annuler</button>
              <button style={{ ...styles.btnConfirm, opacity: gpsCorrection.trim() ? 1 : 0.4 }}
                onClick={enregistrerGps} disabled={saving || !gpsCorrection.trim()}>
                {saving ? 'Enregistrement...' : '✅ Enregistrer la position'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: COULEUR, padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  addBtn: { background: ROUGE, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 },
  searchWrap: { padding: '8px 12px', background: '#fff', borderBottom: '1px solid #eee' },
  searchInput: { width: '100%', padding: '9px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, background: '#f9f9f9', fontFamily: 'system-ui', boxSizing: 'border-box' },
  filtreToggleRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', borderBottom: '1px solid #eee' },
  filtreToggleBtn: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 },
  resetBtn: { padding: '6px 10px', border: '1px solid #F5C6C6', borderRadius: 8, background: '#FCEBEB', color: ROUGE, fontSize: 12, cursor: 'pointer' },
  resetBtn2: { padding: '8px 16px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer', marginTop: 8 },
  filtresPanel: { background: '#fff', borderBottom: '1px solid #eee', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  filtreRow: { display: 'flex', alignItems: 'center', gap: 10 },
  filtreLabel: { fontSize: 12, color: '#666', width: 80, flexShrink: 0 },
  select: { flex: 1, padding: '7px 8px', border: '1px solid #ddd', borderRadius: 8, background: '#f9f9f9', fontSize: 12, color: '#444' },
  listWrap: { flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', color: '#888', padding: '3rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  addClientEmptyBtn: { marginTop: 8, padding: '10px 20px', background: COULEUR, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' },
  clientCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  clientRow: { display: 'flex', alignItems: 'center', gap: 10 },
  clientAv: { width: 38, height: 38, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#185FA5', flexShrink: 0 },
  clientNom: { fontSize: 14, fontWeight: 600, color: COULEUR },
  clientSub: { fontSize: 11, color: '#888', marginTop: 2 },
  potentielBadge: { fontSize: 10, padding: '3px 8px', borderRadius: 99, fontWeight: 600, flexShrink: 0 },
  clientMeta: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' },
  statutBadge: { fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#f0f4f8', color: '#555', border: '1px solid #ddd' },
  telText: { fontSize: 11, color: '#666' },
  assignedWrap: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  assignedPill: { display: 'flex', alignItems: 'center', gap: 4, background: '#EAF3DE', borderRadius: 99, padding: '3px 8px 3px 10px' },
  removeBtn: { background: 'none', border: 'none', color: '#888', fontSize: 11, cursor: 'pointer', padding: '0 2px' },
  nonAssigne: { fontSize: 11, color: '#A32D2D', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  actionsRow: { display: 'flex', gap: 6, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f5f5f5', flexWrap: 'wrap' },
  actionBtn: { flex: 1, padding: '6px 4px', border: '1px solid #eee', borderRadius: 8, background: '#f9f9f9', color: '#444', fontSize: 11, cursor: 'pointer', textAlign: 'center', minWidth: 60 },
  gpsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  gpsOk: { fontSize: 11, color: '#3B6D11' },
  gpsBtn: { padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, background: '#f9f9f9', color: '#666', fontSize: 11, cursor: 'pointer' },
  gpsBtnWarning: { padding: '4px 10px', border: '1px solid #F5C6C6', borderRadius: 6, background: '#FCEBEB', color: '#A32D2D', fontSize: 11, cursor: 'pointer' },
  gpsInstructions: { background: '#f5f5f5', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.5 },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end' },
  modal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' },
  modalTitle: { fontSize: 15, fontWeight: 600, color: COULEUR, marginBottom: 14 },
  commChoice: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1.5px solid #eee', borderRadius: 10, background: '#fff', cursor: 'pointer', marginBottom: 8, textAlign: 'left' },
  commChoiceActive: { border: `1.5px solid ${COULEUR}`, background: '#f0f4f8' },
  commAv: { width: 32, height: 32, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: ROUGE, flexShrink: 0 },
  btnCancel: { flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' },
  btnConfirm: { flex: 2, padding: 11, background: COULEUR, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formLabel: { fontSize: 12, color: '#666', marginBottom: 4 },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', boxSizing: 'border-box' },
  chipBtn: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 11, cursor: 'pointer' },
  chipBtnActive: { border: `1px solid ${COULEUR}`, background: COULEUR, color: '#fff', fontWeight: 600 },
};
